import React, { useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import LaunchSuccessAdvisorModal, { getLaunchPredictionBadgeLabel } from './LaunchSuccessAdvisorModal';
import './modernized_app.css';

const isLaunchPredictionCandidateRow = row => Boolean(
  row?.id &&
  !row.cancelled &&
  !row.actTakeoff &&
  !row.actLand &&
  row.spareStatus !== 'consumed'
);

const getPredictionBand = prediction => prediction?.successBand || 'insufficient';

const getScheduledTakeoffDate = row => {
  if (row?.schedTakeoffUtc) {
    const value = new Date(row.schedTakeoffUtc);
    if (!Number.isNaN(value.getTime())) return value;
  }

  if (row?.takeoffDate && row?.schedTakeoff) {
    const datePart = String(row.takeoffDate).slice(0, 10);
    const value = new Date(`${datePart}T${row.schedTakeoff}`);
    if (!Number.isNaN(value.getTime())) return value;
  }

  if (row?.takeoffDate) {
    const value = new Date(`${String(row.takeoffDate).slice(0, 10)}T00:00`);
    if (!Number.isNaN(value.getTime())) return value;
  }

  return null;
};

const formatScheduledTakeoff = row => {
  const scheduled = getScheduledTakeoffDate(row);
  if (!scheduled) return row?.schedTakeoff || '--';
  return scheduled.toLocaleString([], {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatScheduledTakeoffDate = row => {
  const scheduled = getScheduledTakeoffDate(row);
  if (!scheduled) return row?.takeoffDate || '--';
  return scheduled.toLocaleDateString([], {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

const formatScheduledTakeoffTime = row => {
  const scheduled = getScheduledTakeoffDate(row);
  if (!scheduled) return row?.schedTakeoff || '--';
  return scheduled.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatTimeRemaining = prediction => {
  const minutes = prediction?.factors?.timeRemainingMinutes;
  if (minutes === null || minutes === undefined || Number.isNaN(Number(minutes))) return '--';
  const value = Number(minutes);
  if (value <= 0) return 'Past due';
  if (value < 60) return `${Math.round(value)} min`;
  const hours = Math.floor(value / 60);
  const remainingMinutes = Math.round(value % 60);
  return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

const getRiskLabel = prediction => {
  const band = getPredictionBand(prediction);
  if (!prediction) return 'Pending';
  if (band === 'critical') return 'Past due / no-go';
  if (band === 'red') return 'High risk';
  if (band === 'gold') return 'Watch';
  if (band === 'green') return 'Likely';
  return 'Needs history';
};

const getRiskFilterMatch = (item, filter) => {
  if (filter === 'all') return true;
  const band = getPredictionBand(item.prediction);
  if (filter === 'high-risk') return band === 'critical' || band === 'red';
  if (filter === 'watch') return band === 'gold';
  if (filter === 'likely') return band === 'green';
  if (filter === 'history') return !item.prediction || band === 'insufficient';
  return true;
};

const buildSearchText = row => [
  row?.tail,
  row?.priority,
  row?.lineNum,
  row?.callSign,
  row?.spot,
  row?.missionType,
  row?.acftStatus,
].filter(Boolean).join(' ').toLowerCase();

const formatAdvisorTimestamp = value => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatUsageValue = value => Number(value || 0).toLocaleString();

const formatUsageWithLimit = (used, limit, label) => {
  const normalizedLimit = Number(limit || 0);
  if (normalizedLimit > 0) {
    return `${formatUsageValue(used)}/${formatUsageValue(normalizedLimit)} ${label}`;
  }
  return `${formatUsageValue(used)} ${label}`;
};

const buildAdvisorPdfFilename = generatedAt => {
  const parsed = generatedAt ? new Date(generatedAt) : new Date();
  const value = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  const datePart = [
    value.getFullYear(),
    String(value.getMonth() + 1).padStart(2, '0'),
    String(value.getDate()).padStart(2, '0'),
  ].join('-');
  const timePart = [
    String(value.getHours()).padStart(2, '0'),
    String(value.getMinutes()).padStart(2, '0'),
  ].join('');
  return `launch-ai-advisor-${datePart}_${timePart}.pdf`;
};

function LaunchAiAdvisorCard({ advisor, isLoading, error, onRun }) {
  const advisorCardRef = useRef(null);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [pdfExportError, setPdfExportError] = useState('');
  const actions = advisor?.immediateActions || [];
  const reallocations = advisor?.personnelReallocations || [];
  const aircraftRecommendations = advisor?.aircraftRecommendations || [];
  const decisionPoints = advisor?.decisionPoints || [];
  const watchItems = advisor?.watchItems || [];
  const evidence = advisor?.evidenceUsed || [];
  const dataGaps = advisor?.dataGaps || [];
  const usage = advisor?.aiMonthlyUsage;

  const exportToPdf = async () => {
    if (!advisor || !advisorCardRef.current || isExportingPdf) return;

    setPdfExportError('');
    setIsExportingPdf(true);
    const detailsElements = Array.from(advisorCardRef.current.querySelectorAll('details'));
    const previousDetailsState = detailsElements.map(element => element.open);

    try {
      detailsElements.forEach(element => {
        element.open = true;
      });
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

      const canvas = await html2canvas(advisorCardRef.current, {
        backgroundColor: '#f4f9fd',
        scale: 1.75,
        useCORS: true,
        logging: false,
      });
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'letter',
        compress: true,
      });
      const margin = 32;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const availableWidth = pageWidth - (margin * 2);
      const availableHeight = pageHeight - (margin * 2);
      const sourcePageHeight = Math.max(
        1,
        Math.floor((availableHeight * canvas.width) / availableWidth)
      );
      const totalPages = Math.ceil(canvas.height / sourcePageHeight);

      for (let sourceY = 0, pageNumber = 1; sourceY < canvas.height; pageNumber += 1) {
        const sliceHeight = Math.min(sourcePageHeight, canvas.height - sourceY);
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeight;
        const context = pageCanvas.getContext('2d');
        if (!context) throw new Error('Unable to create the PDF image.');
        context.drawImage(
          canvas,
          0,
          sourceY,
          canvas.width,
          sliceHeight,
          0,
          0,
          canvas.width,
          sliceHeight
        );

        if (pageNumber > 1) pdf.addPage();
        const renderedHeight = (sliceHeight * availableWidth) / canvas.width;
        pdf.addImage(
          pageCanvas.toDataURL('image/png'),
          'PNG',
          margin,
          margin,
          availableWidth,
          renderedHeight,
          undefined,
          'FAST'
        );
        pdf.setFontSize(8);
        pdf.setTextColor(90, 105, 122);
        pdf.text(
          `24-Hour AI Advisor | Page ${pageNumber} of ${totalPages}`,
          pageWidth - margin,
          pageHeight - 12,
          { align: 'right' }
        );
        sourceY += sliceHeight;
      }

      pdf.setProperties({
        title: '24-Hour AI Advisor',
        subject: 'Saved Launch Advisor assessment snapshot',
        creator: 'MFE Repair Management',
      });
      pdf.save(buildAdvisorPdfFilename(advisor.generatedAt));
    } catch (exportError) {
      console.error('Failed to export the AI Advisor assessment:', exportError);
      setPdfExportError('The PDF could not be created. Please try the export again.');
    } finally {
      detailsElements.forEach((element, index) => {
        element.open = previousDetailsState[index];
      });
      setIsExportingPdf(false);
    }
  };

  return (
    <aside
      ref={advisorCardRef}
      className={`launch-ai-advisor-card${isExportingPdf ? ' is-pdf-exporting' : ''}`}
      aria-live="polite"
      aria-busy={isExportingPdf || undefined}
    >
      <div className="launch-ai-advisor-card__header">
        <div>
          <span className="launch-ai-advisor-card__eyebrow">Optional AI Assessment</span>
          <h3>24-Hour AI Advisor</h3>
        </div>
        <span className="launch-ai-advisor-card__advisory">Advisory only</span>
      </div>

      {!advisor && !isLoading && !error ? (
        <div className="launch-ai-advisor-card__empty">
          <p>
            Assess launch priorities, milestone risk, critical parts, repair history, spares,
            and personnel availability across the next 24 hours.
          </p>
          <button type="button" onClick={onRun}>Run AI Advisor</button>
        </div>
      ) : null}

      {isLoading ? (
        <div className="launch-ai-advisor-card__loading">
          <strong>Building the 24-hour assessment...</strong>
          <span>Reviewing aircraft, parts, priorities, and shift coverage.</span>
        </div>
      ) : null}

      {error ? (
        <div className="launch-ai-advisor-card__error">
          <strong>AI Advisor could not run.</strong>
          <span>{error}</span>
          <button type="button" onClick={onRun}>Try Again</button>
        </div>
      ) : null}

      {advisor && !isLoading ? (
        <>
          <div className="launch-ai-advisor-card__summary">
            <div>
              <span>Assessment</span>
              <strong>{advisor.overallAssessment || 'Assessment completed.'}</strong>
            </div>
            <div className="launch-ai-advisor-card__confidence">
              <span>Confidence</span>
              <strong>{advisor.confidence || '--'}</strong>
            </div>
            {advisor.confidenceReason ? <p>{advisor.confidenceReason}</p> : null}
          </div>

          <section className="launch-ai-advisor-card__section">
            <h4>Do Next</h4>
            {actions.length > 0 ? (
              <ol className="launch-ai-advisor-actions">
                {actions.slice(0, 5).map((action, index) => (
                  <li key={`${action.rank || index}-${action.action || ''}`}>
                    <span className="launch-ai-advisor-actions__rank">{action.rank || index + 1}</span>
                    <div>
                      <strong>{action.action}</strong>
                      <p>{action.why}</p>
                      <small>
                        {action.executeBy ? `Timing: ${action.executeBy}` : 'Timing not specified'}
                        {action.affectedTails?.length ? ` | Tails: ${action.affectedTails.join(', ')}` : ''}
                      </small>
                      {action.newRisk ? <em>New risk: {action.newRisk}</em> : null}
                    </div>
                  </li>
                ))}
              </ol>
            ) : <p className="launch-ai-advisor-card__muted">No immediate action was recommended.</p>}
          </section>

          {reallocations.length > 0 ? (
            <section className="launch-ai-advisor-card__section">
              <h4>Personnel Moves to Consider</h4>
              <div className="launch-ai-advisor-card__stack">
                {reallocations.slice(0, 4).map((item, index) => (
                  <article key={`${item.personnel || 'skill'}-${index}`}>
                    <strong>{item.personnel || 'Required skill/shop'}</strong>
                    <span>{item.fromAssignment || 'Unassigned'} to {item.toAssignment || 'recommended work'}</span>
                    <p>{item.why}</p>
                    {item.constraints ? <small>Constraint: {item.constraints}</small> : null}
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          {aircraftRecommendations.length > 0 ? (
            <section className="launch-ai-advisor-card__section">
              <h4>Aircraft and Parts</h4>
              <div className="launch-ai-advisor-card__stack">
                {aircraftRecommendations.slice(0, 5).map((item, index) => (
                  <article key={`${item.tail || 'tail'}-${index}`}>
                    <strong>Tail {item.tail || '--'}{item.priority ? ` | Priority ${item.priority}` : ''}</strong>
                    <p>{item.recommendation}</p>
                    {item.partsImpact ? <small>Parts: {item.partsImpact}</small> : null}
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          {decisionPoints.length > 0 ? (
            <section className="launch-ai-advisor-card__section">
              <h4>Decision Points</h4>
              <div className="launch-ai-advisor-card__timeline">
                {decisionPoints.slice(0, 5).map((item, index) => (
                  <div key={`${item.time || 'time'}-${index}`}>
                    <strong>{item.time || 'Next checkpoint'}</strong>
                    <span>{item.trigger}</span>
                    <p>{item.action}{item.affectedTail ? ` | Tail ${item.affectedTail}` : ''}</p>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {watchItems.length > 0 ? (
            <section className="launch-ai-advisor-card__section">
              <h4>Watch</h4>
              <ul className="launch-ai-advisor-card__plain-list">
                {watchItems.slice(0, 5).map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
              </ul>
            </section>
          ) : null}

          {evidence.length > 0 || dataGaps.length > 0 ? (
            <details className="launch-ai-advisor-card__details">
              <summary>Evidence and data gaps</summary>
              {evidence.length > 0 ? (
                <>
                  <strong>Evidence used</strong>
                  <ul>{evidence.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}</ul>
                </>
              ) : null}
              {dataGaps.length > 0 ? (
                <>
                  <strong>Data gaps</strong>
                  <ul>{dataGaps.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}</ul>
                </>
              ) : null}
            </details>
          ) : null}

          <div className="launch-ai-advisor-card__footer">
            <span>{formatAdvisorTimestamp(advisor.generatedAt) || 'Saved assessment'}</span>
            {usage ? (
              <span>
                {formatUsageWithLimit(usage.requestCount, advisor.aiMonthlyRequestLimit, 'runs')}
                {' | '}
                {formatUsageWithLimit(usage.totalTokens, advisor.aiMonthlyTokenLimit, 'tokens')}
              </span>
            ) : null}
            <div className="launch-ai-advisor-card__footer-actions">
              <button type="button" onClick={exportToPdf} disabled={isExportingPdf}>
                {isExportingPdf ? 'Creating PDF...' : 'Export PDF'}
              </button>
              <button type="button" onClick={onRun} disabled={isExportingPdf}>Run Again</button>
            </div>
          </div>
          {pdfExportError ? (
            <div className="launch-ai-advisor-card__pdf-error" role="alert">{pdfExportError}</div>
          ) : null}
        </>
      ) : null}
    </aside>
  );
}

export default function LaunchAdvisorTab({
  api,
  user,
  selectedBase,
  globalView,
  isEnabled,
  isEntitlementLoading,
  onOpenUpgrade,
}) {
  const [atoRows, setAtoRows] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [riskFilter, setRiskFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [launchFeedback, setLaunchFeedback] = useState({ accepted: '', feedbackAction: '', feedbackReason: '' });
  const [aiAdvisor, setAiAdvisor] = useState(null);
  const [isAiAdvisorLoading, setIsAiAdvisorLoading] = useState(false);
  const [aiAdvisorError, setAiAdvisorError] = useState('');
  const [flightAiAssessment, setFlightAiAssessment] = useState(null);
  const [isFlightAiLoading, setIsFlightAiLoading] = useState(false);
  const [flightAiError, setFlightAiError] = useState('');
  const [canOverrideWeather, setCanOverrideWeather] = useState(false);
  const [isWeatherOverrideSaving, setIsWeatherOverrideSaving] = useState(false);

  const location = selectedBase || user?.location || '';

  useEffect(() => {
    if (!api || !user?.email || !location) {
      setAtoRows([]);
      setPredictions([]);
      setError('');
      setAccessDenied(false);
      setCanOverrideWeather(false);
      return undefined;
    }

    let isStale = false;
    const loadAdvisorData = async () => {
      try {
        setIsLoading(true);
        setError('');
        setAiAdvisorError('');
        setAiAdvisor(null);
        const atoRes = await api.get('/get-ato', {
          params: {
            email: user.email,
            location,
            global: globalView,
          },
        });
        const rows = atoRes.data?.data || [];
        const sortedRows = [...rows].sort((a, b) => {
          const aTime = getScheduledTakeoffDate(a)?.getTime() ?? Number.MAX_SAFE_INTEGER;
          const bTime = getScheduledTakeoffDate(b)?.getTime() ?? Number.MAX_SAFE_INTEGER;
          if (aTime !== bTime) return aTime - bTime;
          return String(a.lineNum || '').localeCompare(String(b.lineNum || ''), undefined, { numeric: true });
        });
        const candidateIds = sortedRows.filter(isLaunchPredictionCandidateRow).map(row => row.id);
        let predictionRows = [];

        const predictionRes = await api.get('/launch-success-predictions', {
          params: {
            email: user.email,
            location,
            global: globalView,
            atoIds: candidateIds.length > 0 ? candidateIds.join(',') : '__access_check__',
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        });
        predictionRows = candidateIds.length > 0 ? (predictionRes.data?.data || []) : [];

        if (!isStale) {
          setAccessDenied(false);
          setAtoRows(sortedRows);
          setPredictions(predictionRows);
          setCanOverrideWeather(Boolean(predictionRes.data?.canOverrideWeather));
        }

        try {
          const savedAdvisorRes = await api.get('/commander-briefs', {
            params: {
              email: user.email,
              location,
              global: false,
              reportType: 'launch_ai_advisor',
              limit: 1,
            },
          });
          const latestSaved = savedAdvisorRes.data?.history?.[0]?.brief || null;
          if (!isStale) setAiAdvisor(latestSaved);
        } catch {
          if (!isStale) setAiAdvisor(null);
        }
      } catch (err) {
        if (!isStale) {
          setAtoRows([]);
          setPredictions([]);
          setCanOverrideWeather(false);
          if (err.response?.status === 403) {
            setAccessDenied(true);
            setError(err.response?.data?.message || 'Upgrade required to use Launch Success Advisor for this location.');
          } else {
            setAccessDenied(false);
            setError(err.response?.data?.message || 'Unable to load Launch Advisor data.');
          }
        }
      } finally {
        if (!isStale) {
          setIsLoading(false);
        }
      }
    };

    loadAdvisorData();
    return () => {
      isStale = true;
    };
  }, [api, user?.email, location, globalView, isEnabled, refreshNonce]);

  const predictionByAtoId = useMemo(() => {
    const map = new Map();
    predictions.forEach(prediction => {
      if (prediction?.atoId) map.set(String(prediction.atoId), prediction);
    });
    return map;
  }, [predictions]);

  const advisorRows = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    return atoRows
      .filter(isLaunchPredictionCandidateRow)
      .map(row => ({
        row,
        prediction: predictionByAtoId.get(String(row.id)) || null,
      }))
      .filter(item => getRiskFilterMatch(item, riskFilter))
      .filter(item => !search || buildSearchText(item.row).includes(search))
      .sort((a, b) => {
        const aTakeoff = getScheduledTakeoffDate(a.row)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        const bTakeoff = getScheduledTakeoffDate(b.row)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        if (aTakeoff !== bTakeoff) return aTakeoff - bTakeoff;
        return String(a.row.lineNum || '').localeCompare(String(b.row.lineNum || ''), undefined, { numeric: true });
      });
  }, [atoRows, predictionByAtoId, riskFilter, searchText]);

  const summary = useMemo(() => {
    const candidateItems = atoRows
      .filter(isLaunchPredictionCandidateRow)
      .map(row => ({ row, prediction: predictionByAtoId.get(String(row.id)) || null }));

    return {
      pending: candidateItems.length,
      highRisk: candidateItems.filter(item => ['critical', 'red'].includes(getPredictionBand(item.prediction))).length,
      watch: candidateItems.filter(item => getPredictionBand(item.prediction) === 'gold').length,
      likely: candidateItems.filter(item => getPredictionBand(item.prediction) === 'green').length,
      needsHistory: candidateItems.filter(item => !item.prediction || getPredictionBand(item.prediction) === 'insufficient').length,
    };
  }, [atoRows, predictionByAtoId]);

  const openPredictionDetails = (row, prediction) => {
    if (!prediction) return;
    setFlightAiAssessment(null);
    setFlightAiError('');
    setSelectedPrediction({
      ...prediction,
      row,
    });
    setLaunchFeedback({
      accepted: prediction.accepted == null ? '' : String(Boolean(prediction.accepted)),
      feedbackAction: prediction.feedbackAction || '',
      feedbackReason: prediction.feedbackReason || '',
    });
  };

  const runAiAdvisor = async () => {
    try {
      setIsAiAdvisorLoading(true);
      setAiAdvisorError('');
      const res = await api.post('/launch-ai-advisor', {
        email: user.email,
        location,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        scope: '24h',
      });
      setAiAdvisor(res.data?.advisor || null);
    } catch (err) {
      setAiAdvisorError(err.response?.data?.message || 'Unable to run the 24-hour AI Advisor.');
    } finally {
      setIsAiAdvisorLoading(false);
    }
  };

  const runFlightAiAssessment = async () => {
    if (!selectedPrediction?.row?.id) return;
    try {
      setIsFlightAiLoading(true);
      setFlightAiError('');
      const res = await api.post('/launch-ai-advisor', {
        email: user.email,
        location,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        scope: 'flight',
        atoId: selectedPrediction.row.id,
      });
      setFlightAiAssessment(res.data?.advisor || null);
    } catch (err) {
      setFlightAiError(err.response?.data?.message || 'Unable to run the individual AI assessment.');
    } finally {
      setIsFlightAiLoading(false);
    }
  };

  const submitLaunchPredictionFeedback = async () => {
    if (!selectedPrediction?.id || launchFeedback.accepted === '') {
      setMessage('Select accept or reject before saving feedback.');
      window.setTimeout(() => setMessage(''), 3000);
      return;
    }

    try {
      const res = await api.post('/launch-success-prediction-feedback', {
        email: user.email,
        location,
        predictionId: selectedPrediction.id,
        accepted: launchFeedback.accepted === 'true',
        feedbackAction: launchFeedback.feedbackAction,
        feedbackReason: launchFeedback.feedbackReason,
      });
      const updated = res.data.prediction;
      setSelectedPrediction(prev => ({ ...prev, ...updated }));
      setPredictions(prev => prev.map(item => item.id === updated.id ? updated : item));
      setMessage('Launch prediction feedback saved.');
      window.setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err.response?.data?.message || 'Unable to save launch prediction feedback.');
      window.setTimeout(() => setMessage(''), 4000);
    }
  };

  const submitWeatherOverride = async (overrideFactor, reason) => {
    if (!selectedPrediction?.id) return;
    try {
      setIsWeatherOverrideSaving(true);
      const res = await api.post('/launch-success-weather-override', {
        email: user.email,
        location,
        predictionId: selectedPrediction.id,
        overrideFactor,
        reason,
      });
      const updated = res.data.prediction;
      setSelectedPrediction(prev => ({ ...prev, ...updated, row: prev?.row }));
      setPredictions(prev => prev.map(item => item.id === updated.id ? updated : item));
      setMessage('Weather override saved.');
      window.setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err.response?.data?.message || 'Unable to save weather override.');
      window.setTimeout(() => setMessage(''), 4000);
    } finally {
      setIsWeatherOverrideSaving(false);
    }
  };

  if (isEntitlementLoading) {
    return (
      <div className="launch-advisor-tab">
        <section className="reports-panel launch-advisor-access-card">
          <div>
            <div className="reports-eyebrow">Launch Success Advisor</div>
            <h2>Checking Launch Advisor access</h2>
            <p>Loading the subscription settings for {location || 'this location'}.</p>
          </div>
        </section>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="launch-advisor-tab">
        <section className="reports-panel launch-advisor-access-card is-locked">
          <div>
            <div className="reports-eyebrow">Premium Add-On</div>
            <h2>Upgrade to Access Launch Advisor</h2>
            {error ? <p>{error}</p> : null}
            <p>
              Launch Advisor consolidates pending sorties into one probability dashboard, highlights high-risk launches,
              and opens the same rationale view used by the Fly Schedule percent badge.
            </p>
          </div>
          <button type="button" onClick={onOpenUpgrade}>
            Upgrade to Access
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className="launch-advisor-tab">
      <header className="launch-advisor-hero ops-page-header">
        <div className="ops-page-header__copy">
          <div className="reports-eyebrow">Premium Decision Dashboard</div>
          <h2 className="ops-page-header__title">Launch Advisor</h2>
          <p className="status-tab__subtitle">Shows probability of an on-time launch for scheduled sorties (an actual take-off time not yet entered).</p>
        </div>
        <div className="status-tab__header-actions launch-advisor-header-actions">
          <button
            type="button"
            className="launch-advisor-ai-button"
            onClick={runAiAdvisor}
            disabled={isAiAdvisorLoading || isLoading}
          >
            {isAiAdvisorLoading ? 'Running AI Advisor...' : 'Run AI Advisor'}
          </button>
          <button type="button" className="reports-export-button" onClick={() => setRefreshNonce(value => value + 1)} disabled={isLoading}>
            {isLoading ? 'Refreshing...' : 'Refresh Advisor'}
          </button>
        </div>
      </header>

      {message ? <div className="admin-message is-success">{message}</div> : null}
      {error ? <div className="admin-message is-error">{error}</div> : null}

      <section className="launch-advisor-kpi-grid" aria-label="Launch Advisor summary">
        <div className="launch-advisor-kpi">
          <span>Pending</span>
          <strong>{summary.pending}</strong>
        </div>
        <div className="launch-advisor-kpi is-risk">
          <span>High Risk</span>
          <strong>{summary.highRisk}</strong>
        </div>
        <div className="launch-advisor-kpi is-watch">
          <span>Watch</span>
          <strong>{summary.watch}</strong>
        </div>
        <div className="launch-advisor-kpi is-likely">
          <span>Likely</span>
          <strong>{summary.likely}</strong>
        </div>
        <div className="launch-advisor-kpi is-history">
          <span>Needs History</span>
          <strong>{summary.needsHistory}</strong>
        </div>
      </section>

      <section className="reports-panel launch-advisor-panel">
        <div className="launch-advisor-controls">
          <div>
            <h3>Pending Launch Decisions</h3>
            <p>Click a row to see the rationale, similar prior jobs, launch-flow penalties, and feedback controls.</p>
          </div>
          <div className="launch-advisor-filter-row">
            <input
              type="search"
              value={searchText}
              onChange={event => setSearchText(event.target.value)}
              placeholder="Search tail, line, call sign, spot"
            />
            <select value={riskFilter} onChange={event => setRiskFilter(event.target.value)}>
              <option value="all">All risk bands</option>
              <option value="high-risk">High risk</option>
              <option value="watch">Watch</option>
              <option value="likely">Likely</option>
              <option value="history">Needs history</option>
            </select>
          </div>
        </div>

        <div className="launch-advisor-decision-grid">
          <div className="launch-advisor-table-column">
            <div className="reports-table-shell launch-advisor-table-shell">
              <table className="reports-table launch-advisor-table">
                <thead>
                  <tr>
                    <th>Launch %</th>
                    <th>Risk</th>
                    <th>Local Date</th>
                    <th>Local T/O</th>
                    <th>Line</th>
                    <th>Tail</th>
                    <th>Priority</th>
                    <th>Call Sign</th>
                    <th>Spot</th>
                    <th>Time</th>
                    <th>Rationale</th>
                  </tr>
                </thead>
                <tbody>
                  {advisorRows.length > 0 ? advisorRows.map(item => (
                    <tr
                      key={item.row.id}
                      className={item.prediction ? 'is-clickable' : ''}
                      onClick={() => openPredictionDetails(item.row, item.prediction)}
                    >
                      <td>
                        <span className={`launch-success-badge launch-advisor-table-badge is-${getPredictionBand(item.prediction)}`}>
                          {getLaunchPredictionBadgeLabel(item.prediction)}
                        </span>
                      </td>
                      <td><span className={`launch-advisor-risk-pill is-${getPredictionBand(item.prediction)}`}>{getRiskLabel(item.prediction)}</span></td>
                      <td title={`Scheduled takeoff: ${formatScheduledTakeoff(item.row)}`}>{formatScheduledTakeoffDate(item.row)}</td>
                      <td title={`Scheduled takeoff: ${formatScheduledTakeoff(item.row)}`}>{formatScheduledTakeoffTime(item.row)}</td>
                      <td>{item.row.lineNum || '--'}</td>
                      <td>{item.row.tail || '--'}</td>
                      <td>{item.row.priority || '--'}</td>
                      <td>{item.row.callSign || '--'}</td>
                      <td>{item.row.spot || '--'}</td>
                      <td>{formatTimeRemaining(item.prediction)}</td>
                      <td>{item.prediction?.recommendation || 'Prediction pending.'}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={11} className="reports-empty">
                        {isLoading ? 'Loading launch probabilities...' : 'No pending launch rows match the current filters.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <LaunchAiAdvisorCard
            advisor={aiAdvisor}
            isLoading={isAiAdvisorLoading}
            error={aiAdvisorError}
            onRun={runAiAdvisor}
          />
        </div>
      </section>

      <LaunchSuccessAdvisorModal
        prediction={selectedPrediction}
        feedback={launchFeedback}
        onFeedbackChange={setLaunchFeedback}
        onSaveFeedback={submitLaunchPredictionFeedback}
        aiAssessment={flightAiAssessment}
        isAiAssessmentLoading={isFlightAiLoading}
        aiAssessmentError={flightAiError}
        onRunAiAssessment={runFlightAiAssessment}
        canManageWeatherOverride={canOverrideWeather}
        isWeatherOverrideSaving={isWeatherOverrideSaving}
        onWeatherOverrideChange={submitWeatherOverride}
        onClose={() => {
          setSelectedPrediction(null);
          setFlightAiAssessment(null);
          setFlightAiError('');
        }}
      />
    </div>
  );
}
